/**
 * The message bus between a page and the course iframe.
 *
 * The overlay inside the frame (`web/public/rt/review-overlay.js`) speaks a
 * small protocol: it emits `ready`, `blocks`, `scroll`, `comment` and
 * `select`; it accepts `counts` and `select`. Both pages need all of it, and
 * both used to wire it by hand — the reviewer page not at all, which is one of
 * the ways comments failed to show there.
 *
 * Two rules are load-bearing:
 *
 *   · The frame is sandboxed into an *opaque* origin, so `e.origin` is the
 *     string "null" and proves nothing — every sandboxed frame shares it,
 *     including the nested one a CustomCode block runs in. Identity is the
 *     window handle: `e.source === frame.contentWindow`.
 *
 *   · Counts are sent when the overlay says it is listening (`ready`) AND
 *     whenever the threads change. Either alone misses: the first load
 *     usually beats the frame, and a reload happens with no new `ready`.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ThreadWire } from '../../api';

export interface BlockBox {
  id: string;
  top: number;
  height: number;
  /** The block's own heading, when it has one — the outline's sub-tree text. */
  label?: string;
}

export interface CourseState {
  lesson: number;
  lessons: { index: number; title: string }[];
  boxes: BlockBox[];
  scrollTop: number;
}

const EMPTY: CourseState = { lesson: -1, lessons: [], boxes: [], scrollTop: 0 };

export function useCourseBus(opts: {
  frame: React.RefObject<HTMLIFrameElement | null>;
  threads: ThreadWire[] | null;
  /** Blocks edited since the previous round, for the in-course markers. */
  changed?: string[];
  onComment: (target: { blockId: string; anchorLabel: string }) => void;
  onSelect: (blockId: string) => void;
}): {
  course: CourseState;
  /** Highlight a block in the course and scroll it into view. */
  sendSelect: (blockId: string) => void;
} {
  const { frame, threads, changed, onComment, onSelect } = opts;
  const [course, setCourse] = useState<CourseState>(EMPTY);

  const postCounts = useCallback(() => {
    const w = frame.current?.contentWindow;
    if (!w || !threads) return;
    const counts: Record<string, number> = {};
    for (const t of threads) {
      if (t.scope === 'block' && t.blockId) counts[t.blockId] = (counts[t.blockId] ?? 0) + 1;
    }
    w.postMessage(
      { source: 'mauvely-review', type: 'counts', counts, changed: changed ?? [] },
      '*'
    );
  }, [frame, threads, changed]);

  useEffect(() => {
    postCounts();
  }, [postCounts]);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      const d = e.data as Record<string, unknown>;
      if (d?.source !== 'mauvely-review') return;

      if (d.type === 'ready') {
        postCounts();
      } else if (d.type === 'blocks') {
        setCourse({
          lesson: (d.lesson as number) ?? -1,
          lessons: (d.lessons as CourseState['lessons']) ?? [],
          boxes: (d.blocks as BlockBox[]) ?? [],
          scrollTop: (d.scrollTop as number) ?? 0
        });
      } else if (d.type === 'scroll') {
        setCourse((c) => ({ ...c, scrollTop: (d.scrollTop as number) ?? 0 }));
      } else if (d.type === 'comment') {
        onComment({
          blockId: d.blockId as string,
          anchorLabel: (d.anchorLabel as string) ?? ''
        });
      } else if (d.type === 'select') {
        onSelect(d.blockId as string);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frame, postCounts, onComment, onSelect]);

  const sendSelect = useCallback(
    (blockId: string) => {
      frame.current?.contentWindow?.postMessage(
        { source: 'mauvely-review', type: 'select', blockId },
        '*'
      );
    },
    [frame]
  );

  return { course, sendSelect };
}
